/**
 * m3 echo Frontend - Open-Source-Lizenzen
 *
 * Lists all open-source software used in frontend and backend.
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Link,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface LicenseEntry {
  name: string;
  version: string;
  license: string;
  url: string;
}

const frontendDependencies: LicenseEntry[] = [
  { name: '@emotion/react', version: '11.x', license: 'MIT', url: 'https://github.com/emotion-js/emotion' },
  { name: '@emotion/styled', version: '11.x', license: 'MIT', url: 'https://github.com/emotion-js/emotion' },
  { name: '@headlessui/react', version: '2.x', license: 'MIT', url: 'https://github.com/tailwindlabs/headlessui' },
  { name: '@heroicons/react', version: '2.x', license: 'MIT', url: 'https://github.com/tailwindlabs/heroicons' },
  { name: '@mui/material', version: '5.x', license: 'MIT', url: 'https://github.com/mui/material-ui' },
  { name: '@mui/icons-material', version: '5.x', license: 'MIT', url: 'https://github.com/mui/material-ui' },
  { name: '@tanstack/react-query', version: '5.x', license: 'MIT', url: 'https://github.com/TanStack/query' },
  { name: 'axios', version: '1.x', license: 'MIT', url: 'https://github.com/axios/axios' },
  { name: 'dompurify', version: '3.x', license: 'Apache-2.0', url: 'https://github.com/cure53/DOMPurify' },
  { name: 'react', version: '18.x', license: 'MIT', url: 'https://github.com/facebook/react' },
  { name: 'react-dom', version: '18.x', license: 'MIT', url: 'https://github.com/facebook/react' },
  { name: 'react-markdown', version: '10.x', license: 'MIT', url: 'https://github.com/remarkjs/react-markdown' },
  { name: 'react-router-dom', version: '6.x', license: 'MIT', url: 'https://github.com/remix-run/react-router' },
  { name: 'recharts', version: '2.x', license: 'MIT', url: 'https://github.com/recharts/recharts' },
  { name: 'zustand', version: '4.x', license: 'MIT', url: 'https://github.com/pmndrs/zustand' },
  { name: 'vite', version: '5.x', license: 'MIT', url: 'https://github.com/vitejs/vite' },
  { name: 'typescript', version: '5.x', license: 'Apache-2.0', url: 'https://github.com/microsoft/TypeScript' },
  { name: 'eslint', version: '8.x', license: 'MIT', url: 'https://github.com/eslint/eslint' },
];

const backendDependencies: LicenseEntry[] = [
  { name: 'FastAPI', version: '0.104.x', license: 'MIT', url: 'https://github.com/tiangolo/fastapi' },
  { name: 'Uvicorn', version: '0.24.x', license: 'BSD-3-Clause', url: 'https://github.com/encode/uvicorn' },
  { name: 'Gunicorn', version: '21.x', license: 'MIT', url: 'https://github.com/benoitc/gunicorn' },
  { name: 'Pydantic', version: '2.x', license: 'MIT', url: 'https://github.com/pydantic/pydantic' },
  { name: 'SQLAlchemy', version: '2.x', license: 'MIT', url: 'https://github.com/sqlalchemy/sqlalchemy' },
  { name: 'Alembic', version: '1.x', license: 'MIT', url: 'https://github.com/sqlalchemy/alembic' },
  { name: 'psycopg2', version: '2.x', license: 'LGPL-3.0', url: 'https://github.com/psycopg/psycopg2' },
  { name: 'Celery', version: '5.x', license: 'BSD-3-Clause', url: 'https://github.com/celery/celery' },
  { name: 'Redis (Python)', version: '5.x', license: 'MIT', url: 'https://github.com/redis/redis-py' },
  { name: 'Authlib', version: '1.x', license: 'BSD-3-Clause', url: 'https://github.com/lepture/authlib' },
  { name: 'cryptography', version: '41.x', license: 'Apache-2.0 / BSD-3-Clause', url: 'https://github.com/pyca/cryptography' },
  { name: 'python-jose', version: '3.x', license: 'MIT', url: 'https://github.com/mpdavis/python-jose' },
  { name: 'passlib', version: '1.x', license: 'BSD-3-Clause', url: 'https://github.com/ghedsh/passlib' },
  { name: 'httpx', version: '0.25.x', license: 'BSD-3-Clause', url: 'https://github.com/encode/httpx' },
  { name: 'ReportLab', version: '4.x', license: 'BSD-3-Clause', url: 'https://github.com/MrBitBucket/reportlab-mirror' },
  { name: 'Matplotlib', version: '3.x', license: 'PSF-based', url: 'https://github.com/matplotlib/matplotlib' },
  { name: 'Sentry SDK', version: '1.x', license: 'MIT', url: 'https://github.com/getsentry/sentry-python' },
  { name: 'Prometheus Instrumentator', version: '7.x', license: 'ISC', url: 'https://github.com/trallnag/prometheus-fastapi-instrumentator' },
  { name: 'SlowAPI', version: '0.1.x', license: 'MIT', url: 'https://github.com/laurentS/slowapi' },
  { name: 'python-json-logger', version: '2.x', license: 'BSD-2-Clause', url: 'https://github.com/madzak/python-json-logger' },
];

const infrastructureDependencies: LicenseEntry[] = [
  { name: 'PostgreSQL', version: '16.x', license: 'PostgreSQL License', url: 'https://www.postgresql.org/' },
  { name: 'Redis', version: '7.x', license: 'BSD-3-Clause', url: 'https://github.com/redis/redis' },
  { name: 'Nginx', version: '1.x', license: 'BSD-2-Clause', url: 'https://github.com/nginx/nginx' },
  { name: 'Prometheus', version: 'latest', license: 'Apache-2.0', url: 'https://github.com/prometheus/prometheus' },
  { name: 'Grafana', version: 'latest', license: 'AGPL-3.0', url: 'https://github.com/grafana/grafana' },
];

const LicenseTable: React.FC<{ entries: LicenseEntry[] }> = ({ entries }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>Software</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Lizenz</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map((entry) => (
          <TableRow key={entry.name}>
            <TableCell>
              <Link href={entry.url} target="_blank" rel="noopener noreferrer">
                {entry.name}
              </Link>
            </TableCell>
            <TableCell>{entry.version}</TableCell>
            <TableCell>{entry.license}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const Lizenzen: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: 3,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%', my: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Button
            component={RouterLink}
            to="/impressum"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            Zurück zum Impressum
          </Button>

          <Typography variant="h4" fontWeight={700} gutterBottom>
            Open-Source-Lizenzen
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Diese Anwendung nutzt die folgende Open-Source-Software.
            Wir danken den jeweiligen Entwicklern und Communities.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Frontend
          </Typography>
          <LicenseTable entries={frontendDependencies} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Backend
          </Typography>
          <LicenseTable entries={backendDependencies} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Infrastruktur
          </Typography>
          <LicenseTable entries={infrastructureDependencies} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Lizenzen;
